import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useGirderNotifications } from '../hooks/useGirderNotifications';
import { useAuth } from '../providers/AuthContext';

const GirderUpdatesContext = createContext(null);

// Helper function to map status code to name
const getStatusName = (status) => {
  const statusMap = {
    0: 'inactive',
    1: 'queued',
    2: 'running',
    3: 'success',
    4: 'error',
    5: 'canceled',
  };
  return statusMap[status] || 'unknown';
};

/**
 * Provider component for Girder update notifications (jobs, progress, etc.)
 * Manages job state and WebSocket connection for all Girder notification types
 */
export const GirderUpdatesProvider = ({ children }) => {
  const { token } = useAuth();
  const [jobs, setJobs] = useState(new Map()); // Map<jobId, jobState>
  const { connectionState, onMessage, isConnected } = useGirderNotifications(token);

  // Listen for all Girder notification types
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    const unsubscribe = onMessage((notification) => {
      const { type, data } = notification;

      // Handle job_status notifications (full job document)
      if (type === 'job_status' && data && data._id) {
        const jobId = String(data._id);
        setJobs((prevJobs) => {
          const newJobs = new Map(prevJobs);
          const existingJob = newJobs.get(jobId) || {};
          
          // Merge with existing job state, preserving any additional fields
          newJobs.set(jobId, {
            ...existingJob,
            jobId,
            status: data.status,
            statusName: getStatusName(data.status),
            progress: data.progress || existingJob.progress,
            result: data.result,
            type: data.type,
            title: data.title,
            created: data.created,
            updated: data.updated,
            lastUpdate: Date.now(),
          });
          return newJobs;
        });
      }
      // Handle progress notifications (progress updates)
      // Note: _id is at notification level, not inside data
      else if (type === 'progress' && notification._id) {
        const jobId = String(notification._id);
        setJobs((prevJobs) => {
          const newJobs = new Map(prevJobs);
          const existingJob = newJobs.get(jobId) || {};
          
          // Update progress while preserving other job state
          newJobs.set(jobId, {
            ...existingJob,
            jobId,
            progress: {
              ...existingJob.progress,
              ...data,
              message: data.message || existingJob.progress?.message,
            },
            lastUpdate: Date.now(),
          });
          return newJobs;
        });
      }
      // Handle job_log notifications (log entries)
      else if (type === 'job_log' && data && data._id) {
        const jobId = String(data._id);
        setJobs((prevJobs) => {
          const newJobs = new Map(prevJobs);
          const existingJob = newJobs.get(jobId) || {};
          
          // Append log entry (could be enhanced to store full log array)
          newJobs.set(jobId, {
            ...existingJob,
            jobId,
            lastLogEntry: data.text,
            lastUpdate: Date.now(),
          });
          return newJobs;
        });
      }
      // Handle job_created notifications
      else if (type === 'job_created' && data && data._id) {
        const jobId = String(data._id);
        setJobs((prevJobs) => {
          const newJobs = new Map(prevJobs);
          // Initialize job state if it doesn't exist
          if (!newJobs.has(jobId)) {
            newJobs.set(jobId, {
              jobId,
              status: data.status,
              statusName: getStatusName(data.status),
              progress: data.progress,
              type: data.type,
              title: data.title,
              created: data.created,
              lastUpdate: Date.now(),
            });
          }
          return newJobs;
        });
      }
    });

    return unsubscribe;
  }, [isConnected, onMessage]);

  // Get job status
  const getJobStatus = useCallback((jobId) => {
    return jobs.get(String(jobId)) || null;
  }, [jobs]);

  // Subscribe to job updates (for components that want to track a specific job)
  const subscribeToJob = useCallback((jobId, callback, options = {}) => {
    const { jobType = null } = options;
    const jobIdStr = String(jobId);
    
    // Return current state immediately if available
    const currentState = jobs.get(jobIdStr);
    if (currentState && callback) {
      // Filter by job type if specified
      if (!jobType || currentState.type === jobType) {
        callback(currentState);
      }
    }

    // Set up listener for future updates
    const unsubscribe = onMessage((notification) => {
      const { type, data } = notification;
      let jobState = null;
      let notificationJobId = null;

      // Extract job ID from different notification types
      if (type === 'job_status' && data && data._id) {
        notificationJobId = String(data._id);
        if (notificationJobId === jobIdStr) {
          // Filter by job type if specified
          if (!jobType || data.type === jobType) {
            jobState = {
              jobId: notificationJobId,
              status: data.status,
              statusName: getStatusName(data.status),
              progress: data.progress,
              result: data.result,
              type: data.type,
              title: data.title,
              lastUpdate: Date.now(),
            };
          }
        }
      } else if (type === 'progress' && notification._id) {
        // Note: _id is at notification level for progress notifications, not inside data
        notificationJobId = String(notification._id);
        if (notificationJobId === jobIdStr) {
          const existingJob = jobs.get(jobIdStr);
          // Progress notifications don't have type info, so we accept them if:
          // 1. No jobType filter specified, OR
          // 2. Job doesn't exist yet (we'll accept it and filter later when job_status arrives), OR
          // 3. Existing job type matches the filter
          // Note: We accept progress even without existingJob to allow progress updates before job_status arrives
          if (!jobType || !existingJob || existingJob.type === jobType) {
            // Merge progress data - data from progress notification has total/current directly
            const mergedProgress = {
              ...existingJob?.progress,
              ...data,
            };
            
            console.log(`[GirderUpdatesContext] Progress notification for job ${jobIdStr}:`, {
              existingJobType: existingJob?.type,
              filterJobType: jobType,
              progressData: data,
              mergedProgress,
              hasTotal: typeof mergedProgress.total === 'number',
              hasCurrent: typeof mergedProgress.current === 'number',
            });
            
            jobState = {
              ...existingJob,
              jobId: notificationJobId,
              progress: mergedProgress,
              // Preserve status from existing job if available
              status: existingJob?.status,
              statusName: existingJob?.statusName,
              lastUpdate: Date.now(),
            };
          } else {
            console.log(`[GirderUpdatesContext] Progress notification filtered out for job ${jobIdStr}:`, {
              existingJobType: existingJob?.type,
              filterJobType: jobType,
            });
          }
        }
      } else if (type === 'job_log' && data && data._id) {
        // Note: _id is inside data for job_log notifications
        notificationJobId = String(data._id);
        if (notificationJobId === jobIdStr) {
          const existingJob = jobs.get(jobIdStr);
          if (!jobType || !existingJob || existingJob.type === jobType) {
            jobState = {
              ...existingJob,
              jobId: notificationJobId,
              lastLogEntry: data.text,
              lastUpdate: Date.now(),
            };
          }
        }
      }

      if (jobState && callback) {
        callback(jobState);
      }
    });

    return unsubscribe;
  }, [jobs, onMessage]);

  // Clear job state (for cleanup)
  const clearJob = useCallback((jobId) => {
    setJobs((prevJobs) => {
      const newJobs = new Map(prevJobs);
      newJobs.delete(String(jobId));
      return newJobs;
    });
  }, []);

  const value = {
    jobs: Array.from(jobs.values()),
    getJobStatus,
    subscribeToJob,
    clearJob,
    connectionState,
    isConnected,
  };

  return (
    <GirderUpdatesContext.Provider value={value}>
      {children}
    </GirderUpdatesContext.Provider>
  );
};

/**
 * Hook to use Girder updates context
 */
export const useGirderUpdates = () => {
  const context = useContext(GirderUpdatesContext);
  if (!context) {
    throw new Error('useGirderUpdates must be used within GirderUpdatesProvider');
  }
  return context;
};
